import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { fetchMatrix, fetchResources } from '@/api'
import { MatrixArt } from '@/components/art'
import {
    Chip,
    ErrorState,
    Loading,
    PageHead,
    PageShell,
    PrincipleHint,
    Section,
    StatCard,
    Tooltip,
} from '@/components/common'
import { useAsync } from '@/hooks'
import { useI18n } from '@/i18n'
import { usePrinciple } from '@/principles'
import type { DeclarationStatus, Fip, MatrixRow } from '@/types'

const statuses: DeclarationStatus[] = ['in-use', 'planned', 'under-consideration', 'to-be-replaced']

/** Above this many profiles the picker gets its own filter field. */
const PICKER_FILTER_FROM = 9

type RowCell = {
    fip: Fip
    declared: { resource: string; status: DeclarationStatus }[]
    /** Declares a resource that no other selected profile beats for this principle. */
    top: boolean
}

type RowAnalysis = {
    row: MatrixRow
    cells: RowCell[]
    /** Resources by the number of selected profiles declaring them, most shared first. */
    ranked: { resource: string; fips: RowCell[] }[]
    /** How many profiles declare the most shared resource; 2+ means convergence. */
    topCount: number
}

function analyseRow(row: MatrixRow, fips: Fip[], allFips: Fip[]): RowAnalysis {
    const raw = fips.map((fip) => {
        const cell = row.cells[allFips.indexOf(fip)]
        return {
            fip,
            top: false,
            declared: cell.resources.map((resource, index) => ({
                resource,
                status: cell.statuses[index],
            })),
        }
    })
    const users = new Map<string, RowCell[]>()
    for (const cell of raw) {
        for (const resource of new Set(cell.declared.map((item) => item.resource))) {
            users.set(resource, [...(users.get(resource) ?? []), cell])
        }
    }
    const ranked = [...users.entries()]
        .map(([resource, cells]) => ({ resource, fips: cells }))
        .sort((a, b) => b.fips.length - a.fips.length)
    const topCount = ranked[0]?.fips.length ?? 0
    const cells = raw.map((cell) => ({
        ...cell,
        top:
            topCount > 1 &&
            cell.declared.some((item) => users.get(item.resource)?.length === topCount),
    }))
    return { row, cells, ranked, topCount }
}

function fipLabel(fip: Fip, localized: (text: Fip['title']) => string): string {
    return fip.acronym ?? localized(fip.title)
}

function PrincipleLabel({ row }: { row: MatrixRow }) {
    const { localized } = useI18n()
    const principle = usePrinciple(row.principle)
    return (
        <Tooltip content={principle ? <PrincipleHint principle={principle} /> : row.principle}>
            <span className="heat__principle">
                <span className={`matrix__dot matrix__dot--${row.group}`} />
                <code>{row.principle}</code>
                <span className="heat__title">{localized(row.title)}</span>
            </span>
        </Tooltip>
    )
}

function CompactMatrix({
    analyses,
    fips,
    selected,
    onSelect,
    names,
}: {
    analyses: RowAnalysis[]
    fips: Fip[]
    selected: string
    onSelect: (principle: string) => void
    names: Map<string, string>
}) {
    const { t, localized, statusName } = useI18n()
    return (
        <div className="heat-wrap">
            <table className="heat">
                <thead>
                    <tr>
                        <th scope="col">{t('matrix.principle')}</th>
                        {fips.map((fip) => (
                            <th key={fip.id} scope="col" className="heat__fip">
                                <Link to={`/profiles/${fip.id}`} title={localized(fip.title)}>
                                    {fipLabel(fip, localized)}
                                </Link>
                            </th>
                        ))}
                        <th scope="col" className="heat__top">
                            {t('matrix.mostShared')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {analyses.map(({ row, cells, ranked, topCount }) => (
                        <tr
                            key={row.principle}
                            className={row.principle === selected ? 'heat__row--selected' : ''}
                            onClick={() => onSelect(row.principle)}
                        >
                            <th scope="row">
                                <button
                                    type="button"
                                    className="heat__button"
                                    aria-pressed={row.principle === selected}
                                    onClick={() => onSelect(row.principle)}
                                >
                                    <PrincipleLabel row={row} />
                                </button>
                            </th>
                            {cells.map((cell) => (
                                <td key={cell.fip.id}>
                                    <Tooltip
                                        content={
                                            <>
                                                <strong>{fipLabel(cell.fip, localized)}</strong>
                                                {cell.declared.length ? (
                                                    cell.declared.map((item, index) => (
                                                        <span key={index} className="tip__line">
                                                            {names.get(item.resource) ??
                                                                item.resource}{' '}
                                                            · {statusName(item.status)}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="tip__line">
                                                        {t('fip.gap')}
                                                    </span>
                                                )}
                                            </>
                                        }
                                    >
                                        <span
                                            className={`heat__cell heat__cell--${
                                                !cell.declared.length
                                                    ? 'gap'
                                                    : cell.top
                                                      ? 'top'
                                                      : 'other'
                                            }`}
                                        >
                                            {cell.declared.length || '–'}
                                        </span>
                                    </Tooltip>
                                </td>
                            ))}
                            <td className="heat__top">
                                {topCount > 1 ? (
                                    <span className="heat__topname">
                                        <span className="heat__title">
                                            {names.get(ranked[0].resource) ?? ranked[0].resource}
                                        </span>
                                        <strong>
                                            {topCount}/{cells.length}
                                        </strong>
                                    </span>
                                ) : (
                                    <span className="empty">{t('matrix.noneShared')}</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

/** What is behind one row: every resource, and who declares it. */
function RowDetail({ analysis, names }: { analysis: RowAnalysis; names: Map<string, string> }) {
    const { t, localized, statusName } = useI18n()
    const principle = usePrinciple(analysis.row.principle)
    const gaps = analysis.cells.filter((cell) => !cell.declared.length)
    return (
        <aside className="card rowdetail">
            <h3>
                <code>{analysis.row.principle}</code> {localized(analysis.row.title)}
            </h3>
            <p className="section__hint">{localized(principle?.explanation)}</p>
            <p className="label" style={{ marginTop: '0.8rem' }}>
                {t('matrix.resourcesFor')}
            </p>
            {analysis.ranked.length ? (
                analysis.ranked.map(({ resource, fips }) => (
                    <div key={resource} className="rowdetail__item">
                        <div className="rowdetail__head">
                            <Link to={`/resources/${resource}`}>
                                {names.get(resource) ?? resource}
                            </Link>
                            <strong>
                                {fips.length}/{analysis.cells.length}
                            </strong>
                        </div>
                        <div className="legend legend--tight">
                            {fips.map((cell) => {
                                const status = cell.declared.find(
                                    (item) => item.resource === resource,
                                )?.status
                                return (
                                    <span key={cell.fip.id} title={status && statusName(status)}>
                                        <span
                                            className={`matrix__marker matrix__marker--${status}`}
                                        />
                                        {fipLabel(cell.fip, localized)}
                                    </span>
                                )
                            })}
                        </div>
                    </div>
                ))
            ) : (
                <p className="empty">{t('matrix.noDeclarations')}</p>
            )}
            {gaps.length && analysis.ranked.length ? (
                <p className="section__hint" style={{ marginTop: '0.8rem' }}>
                    {t('matrix.gapsIn')}:{' '}
                    {gaps.map((cell) => fipLabel(cell.fip, localized)).join(', ')}
                </p>
            ) : null}
        </aside>
    )
}

function FullMatrix({
    analyses,
    fips,
    shared,
    names,
}: {
    analyses: RowAnalysis[]
    fips: Fip[]
    shared: Set<string>
    names: Map<string, string>
}) {
    const { t, localized, statusName } = useI18n()
    return (
        <div className="matrix-wrap">
            <table className="matrix">
                <thead>
                    <tr>
                        <th scope="col">{t('matrix.principle')}</th>
                        {fips.map((fip) => (
                            <th key={fip.id} scope="col">
                                <Link to={`/profiles/${fip.id}`}>{fipLabel(fip, localized)}</Link>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {analyses.map(({ row, cells }) => (
                        <tr key={row.principle}>
                            <th scope="row">
                                <PrincipleLabel row={row} />
                            </th>
                            {cells.map((cell) => (
                                <td key={cell.fip.id}>
                                    {cell.declared.length ? (
                                        <div className="matrix__cell">
                                            {cell.declared.map((item, position) => (
                                                <Link
                                                    key={`${item.resource}-${position}`}
                                                    to={`/resources/${item.resource}`}
                                                    className={`matrix__resource${
                                                        shared.has(item.resource)
                                                            ? ' matrix__resource--shared'
                                                            : ''
                                                    }`}
                                                >
                                                    <span
                                                        className={`matrix__marker matrix__marker--${item.status}`}
                                                        title={statusName(item.status)}
                                                    />
                                                    {names.get(item.resource) ?? item.resource}
                                                </Link>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="empty">—</span>
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export function MatrixPage() {
    const { t, localized, statusName } = useI18n()
    const [params, setParams] = useSearchParams()
    const [pickerFilter, setPickerFilter] = useState('')

    const matrix = useAsync(() => fetchMatrix(), [])
    const resources = useAsync(() => fetchResources(), [])

    if (matrix.error) {
        return (
            <PageShell>
                <ErrorState error={matrix.error} onRetry={matrix.reload} />
            </PageShell>
        )
    }
    if (!matrix.data) {
        return (
            <PageShell>
                <Loading />
            </PageShell>
        )
    }

    const data = matrix.data
    const names = new Map((resources.data ?? []).map((item) => [item.id, item.name]))

    // No `fips` parameter means all profiles; an empty one means none.
    const selectedIds = params.has('fips')
        ? params.getAll('fips').filter(Boolean)
        : data.fips.map((fip) => fip.id)
    const fips = data.fips.filter((fip) => selectedIds.includes(fip.id))
    const view = params.get('view') === 'full' ? 'full' : 'compact'

    const setParam = (key: string, values: string[]) => {
        const next = new URLSearchParams(params)
        next.delete(key)
        for (const value of values) {
            next.append(key, value)
        }
        setParams(next, { replace: true })
    }
    const setSelection = (ids: string[]) => {
        setParam('fips', ids.length === data.fips.length ? [] : ids.length ? ids : [''])
    }
    const toggleFip = (id: string) =>
        setSelection(
            selectedIds.includes(id)
                ? selectedIds.filter((item) => item !== id)
                : [...selectedIds, id],
        )

    const analyses = data.rows.map((row) => analyseRow(row, fips, data.fips))
    const selectedPrinciple = params.get('principle') ?? data.rows[0]?.principle ?? ''
    const selectedAnalysis = analyses.find((item) => item.row.principle === selectedPrinciple)

    const usage = new Map<string, Set<string>>()
    for (const { cells } of analyses) {
        for (const cell of cells) {
            for (const { resource } of cell.declared) {
                usage.set(resource, (usage.get(resource) ?? new Set()).add(cell.fip.id))
            }
        }
    }
    const shared = [...usage.entries()]
        .filter(([, users]) => users.size > 1)
        .sort((a, b) => b[1].size - a[1].size)
    const convergent = analyses.filter((item) => item.topCount > 1).length
    const gaps = analyses.flatMap((item) => item.cells).filter((cell) => !cell.declared.length)

    const pickerNeedle = pickerFilter.trim().toLowerCase()
    const pickable = data.fips.filter((fip) =>
        `${fip.acronym ?? ''} ${fip.title.en} ${fip.title.cs}`.toLowerCase().includes(pickerNeedle),
    )

    return (
        <PageShell>
            <PageHead
                title={t('matrix.title')}
                lead={t('matrix.lead')}
                term="matrix"
                art={<MatrixArt />}
            />

            <details className="explainer" style={{ marginBottom: '1.2rem' }}>
                <summary>{t('matrix.howTo')}</summary>
                <ul>
                    <li>{t('matrix.howTo.cells')}</li>
                    <li>{t('matrix.howTo.gaps')}</li>
                    <li>{t('matrix.howTo.detail')}</li>
                </ul>
            </details>

            <div className="filters card">
                <div className="filters__row">
                    <div className="field field--grow">
                        <label>
                            {t('matrix.select')} ({fips.length}/{data.fips.length})
                        </label>
                        {data.fips.length >= PICKER_FILTER_FROM ? (
                            <input
                                type="search"
                                value={pickerFilter}
                                placeholder={t('matrix.filterProfiles')}
                                aria-label={t('matrix.filterProfiles')}
                                onChange={(event) => setPickerFilter(event.target.value)}
                            />
                        ) : null}
                        <div className="toggle-group picker">
                            <button
                                type="button"
                                className="linkish"
                                onClick={() => setSelection(data.fips.map((fip) => fip.id))}
                            >
                                {t('matrix.all')}
                            </button>
                            <button
                                type="button"
                                className="linkish"
                                onClick={() => setSelection([])}
                            >
                                {t('matrix.none')}
                            </button>
                            {pickable.map((fip) => (
                                <button
                                    key={fip.id}
                                    type="button"
                                    className="toggle"
                                    title={localized(fip.title)}
                                    aria-pressed={selectedIds.includes(fip.id)}
                                    onClick={() => toggleFip(fip.id)}
                                >
                                    {fipLabel(fip, localized)}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="field">
                        <label>{t('matrix.view')}</label>
                        <div className="toggle-group">
                            {(['compact', 'full'] as const).map((item) => (
                                <button
                                    key={item}
                                    type="button"
                                    className="toggle"
                                    aria-pressed={view === item}
                                    onClick={() =>
                                        setParam('view', item === 'full' ? ['full'] : [])
                                    }
                                >
                                    {t(`matrix.view.${item}`)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {fips.length < 2 ? <p className="empty">{t('matrix.selectHint')}</p> : null}

            {fips.length ? (
                <>
                    <div className="grid grid--stats grid--compact">
                        <StatCard
                            compact
                            value={`${fips.length}/${data.fips.length}`}
                            label={t('matrix.stat.profiles')}
                        />
                        <StatCard
                            compact
                            value={`${convergent}/${analyses.length}`}
                            label={t('matrix.stat.principles')}
                        />
                        <StatCard
                            compact
                            value={shared.length}
                            label={t('matrix.stat.resources')}
                        />
                        <StatCard compact value={gaps.length} label={t('matrix.stat.gaps')} />
                    </div>

                    {view === 'compact' ? (
                        <div className="matrix-layout">
                            <div className="matrix-layout__main">
                                <CompactMatrix
                                    analyses={analyses}
                                    fips={fips}
                                    selected={selectedPrinciple}
                                    onSelect={(principle) => setParam('principle', [principle])}
                                    names={names}
                                />
                                <div className="legend">
                                    <span>
                                        <span className="heat__cell heat__cell--top legend__swatch" />
                                        {t('matrix.legend.top')}
                                    </span>
                                    <span>
                                        <span className="heat__cell heat__cell--other legend__swatch" />
                                        {t('matrix.legend.other')}
                                    </span>
                                    <span>
                                        <span className="heat__cell heat__cell--gap legend__swatch" />
                                        {t('matrix.legend.gap')}
                                    </span>
                                </div>
                            </div>
                            {selectedAnalysis ? (
                                <RowDetail analysis={selectedAnalysis} names={names} />
                            ) : null}
                        </div>
                    ) : (
                        <FullMatrix
                            analyses={analyses}
                            fips={fips}
                            shared={new Set(shared.map(([resource]) => resource))}
                            names={names}
                        />
                    )}

                    <div className="legend">
                        <strong>{t('matrix.legend')}:</strong>
                        {statuses.map((status) => (
                            <span key={status}>
                                <span className={`matrix__marker matrix__marker--${status}`} />
                                {statusName(status)}
                            </span>
                        ))}
                    </div>

                    {shared.length ? (
                        <Section title={t('matrix.shared')} hint={t('matrix.sharedHint')}>
                            <div className="chips">
                                {shared.map(([resource, users]) => (
                                    <Link key={resource} to={`/resources/${resource}`}>
                                        <Chip variant="accent">
                                            {names.get(resource) ?? resource} · {users.size}/
                                            {fips.length}
                                        </Chip>
                                    </Link>
                                ))}
                            </div>
                        </Section>
                    ) : null}
                </>
            ) : null}
        </PageShell>
    )
}
